import React, { useState, useEffect, useRef, Suspense } from "react";
import { PropTypes } from "prop-types";
import { connect } from "react-redux";
import reactWrapper from "@harbor/elements/utils/react/wrapper";

import { useI18n } from "../../../i18n/useI18n";

//reducers

//components
import Spinner from "../../../common/Spinner";
import TrendAnalysis from "../../sidebar/TrendAnalysis";
import VqoeLegend from "./VqoeLegend";
import ApplicationsTableView from "../ApplicationsTableView";
import AppHoc from "../../../generics/AppHOC";
import QoeDistributionView from "../QoeDistributionView";
import TrendingAppsView from "../TrendingAppsView";
import TreeMap from "./TreeMap";
import QoEComponent from "../../../common/QoEComponent";
import { useMount, drawerScrollToTop } from "../../../utils/genericCommon";
import { getCustomListApi, getQoeLabel } from "../../../utils/common";

const ErrorComponent = React.lazy(
  () => import("../../../common/ErrorComponent"),
);
const NoDataAvailable = React.lazy(
  () => import("../../../common/NoDataAvailable"),
);

//style
import css from "../applicationsMagneticStyle.less";
import appCss from "../../../AppMagnetic.less";

//utils
import { getUsageConfig, getUsageNQoEMetrics } from "./config/donut/usage";
import { getQoEconfig } from "./config/donut/qoe";
import { formatBytes } from "../../../utils/format";
import { TOGGLER, COLOR, EXPORT_CSV_LABELS } from "../../../utils/enums";
import { downloadAsCSVFile } from "../../../utils/common";

const [
  HbrCard,
  HbrDrawer,
  HbrButton,
  HbrDonutChart,
  HbrTooltip,
  HbrIconButton,
  HbrIcon,
] = reactWrapper([
  "hbr-card",
  "hbr-drawer",
  "hbr-button",
  "hbr-donut-chart",
  "hbr-tooltip",
  "hbr-icon-button",
  "hbr-icon",
]);

const mapStateToProps = (state) => ({
  applications: state.vanalytics.app,
});

const ApplicationExperienceView = ({ reporting = false, ...props }) => {
  const { tViews, tCommon } = useI18n(["views", "common"]);
  const [loader, setLoader] = useState(true);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [chartDataError, setChartDataError] = useState(false);
  const setIntervalRefs = useRef(null);
  const {
    app_data_top_usage,
    app_data_with_family_sla,
    applicationAggregateData,
    selectedOverlay,
  } = props.applications.globalFilter;
  const [appExperienceData, setAppExperienceData] = useState(null);
  const [legendData, setLegendData] = useState({});
  const { toggleView, currentTimeStamp } = props.globalFilter;
  const drawerID = "trend-analysis-drawer";
  const mount = useMount();
  const chartData = useRef({
    accordionChartData: null,
    isEmpty: false,
    error: null,
  });

  useEffect(() => {
    setChartDataError(false);

    if (
      !reporting &&
      applicationAggregateData?.resObj?.errorObject instanceof Object === true
    ) {
      setChartDataError(true);
      setLoader(false);
    }
  }, [applicationAggregateData]);

  useEffect(() => {
    if (mount.initMount) {
      mount.initMount = false;
      if (app_data_top_usage === undefined && selectedOverlay !== null) {
        props.setAppsWithFamilySLA(undefined);
        getCustomListApi(props);
      }
    } else {
      if (
        !reporting &&
        (toggleView === TOGGLER.CARD || toggleView === undefined)
      ) {
        props.setAppsWithFamilySLA(undefined);
        getCustomListApi(props);
      }
    }
  }, [currentTimeStamp]);

  useEffect(() => {
    setLoader(true);

    if (
      app_data_with_family_sla != undefined &&
      app_data_with_family_sla?.length > 0
    ) {
      //case where widget has data
      let top20 = [];
      top20 = app_data_with_family_sla.slice(0, 20);

      top20.forEach((data) => {
        const usagePercentage = (data.usage / app_data_top_usage) * 100;
        data.usage_percentage = usagePercentage;
      });

      if (top20 && top20.length > 0) {
        chartData.current.accordionChartData = top20;
        setAppExperienceData(top20);
        props.isReportTop20Callback?.(true);
      }

      const applicationsUsageNQoEData = getUsageNQoEMetrics(
        app_data_with_family_sla,
        app_data_top_usage,
      );

      let emptyData = true;
      const usage = {
        good: 0,
        fair: 0,
        poor: 0,
        unknown: 0,
      };
      const vqoe = {
        good: 0,
        fair: 0,
        poor: 0,
        unknown: 0,
      };

      if (applicationsUsageNQoEData?.vqoe_summary) {
        const legendQoe = {};
        const legendUsage = {};
        let totalAppCount = 0;

        applicationsUsageNQoEData.vqoe_summary.map((sum) => {
          if (sum.app_count > 0) {
            emptyData = false;
            totalAppCount += sum.app_count;
          }
        });

        if (!emptyData) {
          chartData.current.isEmpty = false;

          applicationsUsageNQoEData.vqoe_summary.map((qoe) => {
            let qualityKey = qoe.vqoe;

            if (qoe.vqoe === COLOR.GRAY) {
              qualityKey = COLOR.UNKNOWN;
            }

            if (qoe.app_count > 0) {
              vqoe[qualityKey] = qoe.app_count;

              legendQoe[qualityKey] = {
                count: qoe.app_count,
                percentage: qoe.vqoe_percentage.toFixed(1),
              };
            }
          });

          if (applicationsUsageNQoEData.usage_summary) {
            applicationsUsageNQoEData.usage_summary.map((u) => {
              let usageKey = u.vqoe;

              if (u.vqoe === COLOR.GRAY) {
                usageKey = COLOR.UNKNOWN;
              }

              usage[usageKey] = u.usage;

              if (u.usage > 0) {
                legendUsage[usageKey] = {
                  usage: formatBytes(u.usage),
                  percentage: u.vqoe_percentage.toFixed(1),
                };
              }
            });
          }
        }

        // set the data for chart 1 (QoE chart) and 2 (total usage)
        chartData.current.vqoe = vqoe;
        chartData.current.usage = usage;
        chartData.current.totalAppCount = totalAppCount;
        chartData.current.legendQoe = legendQoe;
        chartData.current.legendUsage = legendUsage;
        setLegendData({ legendUsage: legendUsage, legendQoe: legendQoe });
        // Notify parent component that data is available for this view for report generation
        props.isReportDonutCallback?.(true);
      }
      setLoader(false);
    } else if (
      Array.isArray(app_data_with_family_sla) &&
      app_data_with_family_sla.length === 0
    ) {
      //case the widget has no data
      chartData.current.isEmpty = true;
      setLoader(false);
      // Notify parent component that nodata is available for this view for report generation
      props.isReportDonutCallback?.(true);
      props.isReportTop20Callback?.(true);
    }
    if (reporting) {
      clearTimeout(setIntervalRefs.current);
    }
  }, [app_data_with_family_sla]);

  useEffect(() => {
    props.setPageToggle(TOGGLER.CARD);
    clearTimeout(setIntervalRefs.current);
  }, []);

  const onOpen = () => {
    setSidebarVisible(true);
  };

  const onClose = (e) => {
    if (e.target.className?.includes("time-filter-select")) {
      e.preventDefault();
    } else {
      if (e.target.id === drawerID) setSidebarVisible(false);
      requestAnimationFrame(() => {
        const wrapper = document.querySelector(".trend-analysis hbr-button");
        if (!wrapper) return;
        const innerButton =
          wrapper.shadowRoot?.querySelector("button") || wrapper;
        innerButton.focus();
      });
    }
  };

  if (toggleView === TOGGLER.LIST) {
    return <ApplicationsTableView />;
  }

  const resetToggle = () => {
    if (props.globalFilter.selectedSite < 0) props.setPageToggle(TOGGLER.MAP);
  };

  const downloadCSV = (sourceKey, fields, fileName, firstHeaderName) => {
    const usageData = chartData?.current?.[sourceKey];
    if (!usageData) return;
    // Convert object to array
    const rows = Object.entries(usageData).map(([key, value]) => {
      let row = { [firstHeaderName]: key };
      fields.forEach((f) => (row[f] = value[f]));
      return row;
    });
    // Header based on dynamic fields
    const header = [firstHeaderName, ...fields];
    const csvContent = [
      header.join(","), // header row
      ...rows.map((row) => header.map((h) => row[h]).join(",")),
    ].join("\n");

    // Trigger file download
    downloadAsCSVFile(csvContent, fileName);
  };

  // Download QoE(Donut) data as CSV
  const downloadQOE = () => {
    downloadCSV(
      "legendQoe",
      ["count", "percentage"],
      EXPORT_CSV_LABELS.APPLICATIONS_QOE,
      "QoE",
    );
  };

  // Download Usage Total(Donut) data as CSV
  const downloadUsageTotal = () => {
    downloadCSV(
      "legendUsage",
      ["usage", "percentage"],
      EXPORT_CSV_LABELS.APPLICATIONS_USAGE_TOTAL,
      "name",
    );
  };

  return (
    <>
      <HbrCard
        id="non-interactive"
        slot="label"
        className="app-experience-widget-hbr-card"
      >
        <div
          className={
            reporting
              ? "application-overview"
              : `${css["app-experience-view"]} ${appCss["generic-drawer-spacing"]}`
          }
          data-cy="appExperienceView"
        >
          {!reporting && (
            <HbrDrawer
              id={drawerID}
              overlay={true}
              label={tViews("appExperienceTrend")}
              open={sidebarVisible}
              onHbr-hide={onClose}
              onHbr-show={drawerScrollToTop}
            >
              {sidebarVisible && <TrendAnalysis />}
            </HbrDrawer>
          )}
          {loader === false && !reporting && (
            <div className="title-block" data-cy="appExperienceViewTitle">
              <span className="hbr-type-h2" role="heading" aria-level={"2"}>
                {tViews("appExperienceApplication")}
              </span>
              {loader === false && (
                <div className="trend-analysis">
                  <HbrButton variant="text" onClick={onOpen}>
                    {tViews("trendingAppsViewTrendAnalysis")}
                  </HbrButton>
                </div>
              )}
            </div>
          )}
          {loader === true && (
            <div className="home-spinner-25">
              <Spinner />
            </div>
          )}
          {chartDataError === true ? (
            <Suspense fallback={<Spinner />}>
              <ErrorComponent
                width={"110px"}
                className={"super-small-dashlet-error"}
                errorCode={applicationAggregateData?.resObj?.errorCode}
                message={applicationAggregateData?.resObj?.message}
              />
            </Suspense>
          ) : chartData.current.isEmpty ? (
            <Suspense fallback={<Spinner />}>
              <div id="isLoaded_chart_nodata">
                <NoDataAvailable text={tViews("applicationDashboardNoData")} />
              </div>
            </Suspense>
          ) : (
            loader === false &&
            chartData.current.accordionChartData && (
              <div
                className={reporting ? "chart-container" : "content-block"}
                data-cy="appExperienceViewContent"
              >
                <>
                  <div className="app-experience-chart-1">
                    <div className="qoe-title" role="heading" aria-level={"3"}>
                      {tViews("appExperienceQOE")}
                      <div>
                        <HbrTooltip
                          content={tViews("appExperienceQualityEperience")}
                          placement="top"
                        >
                          <HbrIconButton
                            noPadding
                            className="qoe-tooltip-icon"
                            name="info"
                            sentiment="neutral"
                          />
                        </HbrTooltip>
                      </div>
                    </div>
                    <HbrDonutChart
                      config={getQoEconfig(chartData.current, tViews)}
                      width="100%"
                      height="100%"
                    >
                      {!reporting && (
                        <div slot="header-action" className="download-icon-qoe">
                          <HbrTooltip content={tViews("downloadCSV")}>
                            <HbrButton
                              size="medium"
                              variant="text"
                              data-testid="DownloadCsvBtn"
                              onClick={downloadQOE}
                              arialabel={
                                tViews("a11yLabels.csvDownloadButton") +
                                tViews("a11yLabels.appExperienceQOE")
                              }
                            >
                              <HbrIcon
                                name="file-csv"
                                label={tViews("downloadCSV")}
                              />
                            </HbrButton>
                          </HbrTooltip>
                        </div>
                      )}
                    </HbrDonutChart>
                    <div className="qoe-legend">
                      {app_data_top_usage &&
                        legendData.legendQoe &&
                        Object.keys(chartData.current.legendQoe).map(
                          (qoe, key) => (
                            <QoEComponent
                              key={key}
                              iconType={qoe}
                              qoeScore={""}
                              qoeTextValue={`${getQoeLabel(qoe, tCommon)} - ${chartData.current.legendQoe[qoe].percentage}% (${chartData.current.legendQoe[qoe].count})`}
                            />
                          ),
                        )}
                    </div>
                  </div>
                  <div className="app-divider"></div>
                  <div className="app-experience-chart-2">
                    <div
                      className="app-usage-title"
                      role="heading"
                      aria-level={"3"}
                    >
                      {tViews("appExperienceUSAGE")}
                    </div>
                    <HbrDonutChart
                      config={getUsageConfig(
                        chartData.current.usage,
                        app_data_top_usage,
                        tViews,
                      )}
                      width="100%"
                      height="100%"
                    >
                      {!reporting && (
                        <div
                          slot="header-action"
                          className="download-icon-usage"
                        >
                          <HbrTooltip content={tViews("downloadCSV")}>
                            <HbrButton
                              size="medium"
                              variant="text"
                              data-testid="DownloadCsvBtn"
                              onClick={downloadUsageTotal}
                              arialabel={
                                tViews("a11yLabels.csvDownloadButton") +
                                tViews("a11yLabels.appExperienceUsage")
                              }
                            >
                              <HbrIcon
                                name="file-csv"
                                label={tViews("downloadCSV")}
                              />
                            </HbrButton>
                          </HbrTooltip>
                        </div>
                      )}
                    </HbrDonutChart>
                    <div className="usage-legend">
                      {app_data_top_usage &&
                        legendData.legendUsage &&
                        Object.keys(chartData.current.legendUsage).map(
                          (usage, key) => (
                            <QoEComponent
                              key={key}
                              iconType={usage}
                              qoeScore={""}
                              qoeTextValue={`${getQoeLabel(usage, tCommon)} - ${chartData.current.legendUsage[usage].percentage}% (${chartData.current.legendUsage[usage].usage})`}
                            />
                          ),
                        )}
                    </div>
                  </div>
                </>
                <TreeMap
                  data={[...appExperienceData]}
                  data-cy="tree-map"
                  reporting={reporting}
                  selectedSite={props?.applications?.globalFilter?.selectedSite}
                  resetFunc={resetToggle}
                  appDataTopUsage={app_data_top_usage}
                ></TreeMap>
              </div>
            )
          )}
          <div className="footer-block" data-cy="appExperienceViewFooter">
            <VqoeLegend />
          </div>
        </div>
      </HbrCard>
      <div className={reporting ? "" : "trending-app-row"}>
        <div className="distribution-app">
          <QoeDistributionView {...props} reporting={reporting} />
        </div>
        <div className="trending-app">
          <TrendingAppsView
            setPageToggle={props.setPageToggle}
            reporting={reporting}
            isReportTrendCallback={props.isReportTrendCallback}
          />
        </div>
      </div>
    </>
  );
};
ApplicationExperienceView.propTypes = {
  applications: PropTypes.object.isRequired,
  reporting: PropTypes.bool,
  timeFilter: PropTypes.object,
  globalFilter: PropTypes.object,
  setAppsWithFamilySLA: PropTypes.func,
  isReportDonutCallback: PropTypes.func,
  isReportTop20Callback: PropTypes.func,
  isReportTrendCallback: PropTypes.func,
  setPageToggle: PropTypes.func,
};

export default connect(
  mapStateToProps,
  null,
)(AppHoc(ApplicationExperienceView));
